<script setup lang="ts">
import { errorString } from '@/utils/helpers/rest-response';
import i18n from '@/localisation/i18n';
import { store as vuex } from '@/store/store';
import { computed, onMounted, shallowRef } from 'vue';
import { useSnackbarManager } from '@/modules/snackbar/services/snackbar-manager';
import { useRouter } from '@/router/router';
import { isEmail, useRules } from '@/composables/vuetify-validation';
import { setInputFocus } from '@/utils/helpers/input-form';

const router = useRouter();
const snackbarManager = useSnackbarManager();

const props = withDefaults(
  defineProps<{
    lostEmail?: string;
    lostMessage?: string;
  }>(),
  { lostEmail: '', lostMessage: '' }
);

const emailAddress = shallowRef(props.lostEmail);
const serviceErrors = shallowRef(props.lostMessage);
const cmpEmailAddress = shallowRef<null | { focus(): void }>(null);

const clientConfig = computed(() => vuex.state.clientConfig!);

function focusEmailInput(): void {
  setInputFocus(cmpEmailAddress.value);
}

onMounted(focusEmailInput);

const onSubmit = () =>
  validateAndSubmit(async () => {
    try {
      await vuex.dispatch('requestPasswordReset', emailAddress.value);

      showSnackbarMessage(
        i18n.t('loginResetPasswordEmailSent', {
          emailAddress: emailAddress.value,
        }) as string,
        i18n.t('loginClose') as string
      );
      await router.push({
        name: 'login',
        params: {
          email: emailAddress.value,
          message: 'loginResetPasswordEmailSent',
        },
      });
    } catch (e) {
      serviceErrors.value = errorString(e);
      focusEmailInput();
    }
  }, focusEmailInput);

function showSnackbarMessage(message: string, actionText: string) {
  snackbarManager.show({ message: message, actionText: actionText });
}

const { rules, validateAndSubmit } = useRules<{ emailAddress: string }>({
  emailAddress: {
    rules: [
      () => ((serviceErrors.value = ''), true),
      (v) => !!v || `${i18n.t('emailAddress')} ${i18n.t('formfield.IsRequired')}`,
      (v, { isSubmitting, wasInvalid }) =>
        (!isSubmitting && !wasInvalid) ||
        !v ||
        isEmail(v, i18n.t('formfield.InvalidEmail').toString()),
    ],
  },
});
</script>

<template>
  <VDialog
    content-class="au-popup-dialog"
    max-width="700px"
    no-click-animation
    overlay-color="secondary"
    overlay-opacity="0.80"
    persistent
    :value="true"
  >
    <VForm @submit.prevent="onSubmit">
      <VCard>
        <VCardTitle>
          <span class="headline">{{
            $t('loginForgotPasswordTitle', { title: clientConfig?.systemTitleShort })
          }}</span>
        </VCardTitle>
        <VCardText>
          <VContainer>
            <VRow>
              <VCol class="message-spacer" cols="12">
                <VAlert v-if="serviceErrors !== ''" dense type="error"
                  >{{ serviceErrors }}<br />{{ $t('loginForgotPasswordError2') }}
                </VAlert>
                <VAlert v-else elevation="2" icon="mdi-information"
                  >{{
                    $t('loginForgotPasswordInfo1', {
                      title: clientConfig.systemTitleShort,
                    })
                  }}<br />{{ $t('loginForgotPasswordInfo2') }}
                </VAlert>
              </VCol>
              <VCol cols="12">
                <VTextField
                  :ref="(r) => (cmpEmailAddress = r)"
                  v-model="emailAddress"
                  autocomplete="username email"
                  :label="$t('loginEmail')"
                  required
                  v-bind="rules.emailAddress"
                ></VTextField>
              </VCol>
            </VRow>
          </VContainer>
        </VCardText>
        <VCardActions>
          <VBtn class="d-none d-sm-flex" color="secondary" text to="/login"> Back to login </VBtn>
          <VSpacer />
          <VBtn color="primary" min-width="150" type="submit">
            <VIcon left>mdi-email</VIcon>
            {{ $t('loginResetButton') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VForm>
  </VDialog>
</template>
